/**
 * Observabilidad admin API — PostHog + Datadog RUM (si está inyectado) + logs Railway vía servidor.
 */

import { trackErrorPostHog } from "@/lib/posthogAnalytics";
import { reportAdminError } from "@/lib/reportAdminError";

export type AdminApiTransport = "railway" | "same-origin" | "unknown";

export interface AdminApiOutcome {
  path: string;
  method: string;
  status: number | "network_error";
  durationMs: number;
  transport: AdminApiTransport;
  attempts: number;
  errorMessage?: string;
}

declare global {
  interface Window {
    DD_RUM?: {
      addAction?: (name: string, context?: Record<string, unknown>) => void;
      addError?: (error: Error, context?: Record<string, unknown>) => void;
    };
  }
}

function inferTransport(url: string): AdminApiTransport {
  if (/railway\.app/i.test(url)) return "railway";
  if (url.startsWith("/") || /delfuocampo\.com/i.test(url)) return "same-origin";
  return "unknown";
}

export function trackAdminApiOutcome(
  requestUrl: string,
  outcome: AdminApiOutcome,
): void {
  const payload = {
    site: "delfuocampo",
    admin_api_path: outcome.path,
    admin_api_method: outcome.method,
    admin_api_status: outcome.status,
    admin_api_duration_ms: outcome.durationMs,
    admin_api_transport: outcome.transport,
    admin_api_attempts: outcome.attempts,
    admin_api_request_url: requestUrl,
    page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
    timestamp: new Date().toISOString(),
  };

  const failed =
    outcome.status === "network_error" ||
    (typeof outcome.status === "number" && outcome.status >= 500);

  try {
    const ph = (
      window as unknown as {
        posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void };
      }
    ).posthog;
    ph?.capture?.(failed ? "admin_api_failure" : "admin_api_success", payload);
  } catch {
    /* */
  }

  if (failed && outcome.errorMessage) {
    trackErrorPostHog(new Error(outcome.errorMessage), {
      ...payload,
      $exception_source: "admin_api_fetch",
    });
    reportAdminError({
      page: payload.page_path ?? "/admin",
      message: outcome.errorMessage,
      apiPath: outcome.path,
      status: outcome.status,
      action: "admin_api_fetch",
      fingerprint: `${outcome.path}:${outcome.status}`,
    });
  }

  try {
    if (failed && outcome.errorMessage) {
      window.DD_RUM?.addError?.(new Error(outcome.errorMessage), payload);
    } else {
      window.DD_RUM?.addAction?.("admin_api_request", payload);
    }
  } catch {
    /* DD_RUM optional */
  }
}

export function transportFromRequestUrl(url: string): AdminApiTransport {
  return inferTransport(url);
}